// Het tabblad Zoekopdrachten van Configuratie (W6 ronde 6; research §3.7).
//
// Uit welke woorden de oogst elke maand zijn zoekvragen bouwt — hoeken,
// reikwijdtes, publicatiewoorden en per sector de sectorwoorden — en per
// vraagfamilie wat die vragen opleverden (de oogst-telemetrie). Alleen lezen:
// de woordenlijsten wijzigen is een owner-besluit dat via de bouw loopt, niet
// via een knop (V6; zie "Niet instelbaar" onder Instellingen).
//
// Geen sleutels op het scherm. De server stuurt bij elke hoek en reikwijdte
// zijn woord (`hoekLabel`, `reikwijdteLabel`: "Trendradar: opkomst", "zonder
// hoek", "zonder reikwijdte") en bij elke zoekmachine ook ("Firecrawl", "Exa",
// "Open overheid", "Andere zoekinstelling"). De woorden horen bij de server
// (HOEK_WOORD in beheer_configuratie.py), niet hier: één as, één woordenlijst,
// één plek. Eén vangnet blijft: kent de server een nieuwe sleutel nog niet, dan
// stuurt hij de sleutel zelf als woord terug. Die tonen we niet, maar we zeggen
// dat er nog geen woord voor is.
//
// De uitkomst per familie is de zin van de server (`tekst`: "260 gevonden, 327
// nieuw na ontdubbelen, …"). Het scherm rekent er niets uit — ook geen "x van
// y": bij de hub- en vlaggenschiplanen kan "nieuw" groter zijn dan "gevonden".

import { useState, type ReactNode } from "react";

import { Card, ReasonBadge } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import type { ConfiguratieResponse, Zoekfamilie } from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { Blok, datum, getal, noot } from "./BronnenStukken";
import { cel, kopCel, Tabel } from "./KostenStukken";
import { knopLicht } from "./stijl";

type Zoek = ConfiguratieResponse["zoekopdrachten"];

/** Zoveel vraagfamilies staan er eerst; de rest op "Toon alle" (de echte
 *  telemetrie heeft er 151). De volgorde is die van de server. */
const EERST = 25;

/** Het woord van de server — tenzij hij de sleutel zelf terugstuurde omdat hij
 *  er nog geen woord voor heeft (of niets stuurde): dan de reden. */
function woordOfReden(label: string | null, sleutel: string | null, wat: string): ReactNode {
  const w = label?.trim() ?? "";
  if (!w || w === sleutel) return <EmptyReason reason={`nog geen woord voor deze ${wat}`} />;
  return w;
}

export function Zoekopdrachten({ z }: { z: Zoek }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <Blok titel="Hoe een zoekvraag ontstaat">
        <Card style={{ padding: "14px 18px" }}>
          <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>
            {z.sjabloonUitleg}
          </p>
          <p style={noot} data-max-vragen>
            {z.maxVragenPerConfig !== null
              ? `Een oogstrun stelt per zoekinstelling ten hoogste ${getal(z.maxVragenPerConfig)} vragen.`
              : "De server noemt geen maximum aantal vragen per run."}
          </p>
        </Card>
      </Blok>

      <Blok titel="De woorden">
        <Card style={{ padding: "4px 18px 8px" }}>
          <Deel titel="Hoeken">
            {z.hoeken.length === 0 ? (
              <EmptyReason reason="de server noemt geen hoeken" />
            ) : (
              z.hoeken.map((h) => (
                <WoordRij key={h.sleutel} naam={woordOfReden(h.label, h.sleutel, "hoek")}>
                  <Woorden woorden={h.termen} leeg="geen woorden bij deze hoek" />
                </WoordRij>
              ))
            )}
          </Deel>
          <Deel titel="Reikwijdte">
            {z.reikwijdtes.length === 0 ? (
              <EmptyReason reason="de server noemt geen reikwijdtes" />
            ) : (
              z.reikwijdtes.map((r) => (
                <WoordRij key={r.sleutel} naam={woordOfReden(r.label, r.sleutel, "reikwijdte")}>
                  <Woorden woorden={r.woorden} leeg="geen woorden bij deze reikwijdte" />
                </WoordRij>
              ))
            )}
          </Deel>
          <Deel titel="Publicatiewoorden">
            <div style={{ padding: "10px 0" }}>
              <Woorden woorden={z.publicatiewoorden} leeg="de server noemt geen publicatiewoorden" />
            </div>
          </Deel>
        </Card>
      </Blok>

      <Blok titel="Sectorwoorden">
        <Card style={{ padding: "4px 18px 8px" }}>
          {z.sectoren.length === 0 ? (
            <div style={{ padding: "12px 0" }}>
              <EmptyReason reason="de server noemt geen sectoren" />
            </div>
          ) : (
            z.sectoren.map((s) => (
              <WoordRij
                key={s.sector}
                naam={
                  <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    {s.label ?? <EmptyReason reason="sector zonder naam" />}
                    {!s.actief && <ReasonBadge text="staat nog niet aan" />}
                  </span>
                }
              >
                {s.termen.length > 0 ? (
                  <Woorden woorden={s.termen} leeg="" />
                ) : (
                  <EmptyReason reason={s.reden ?? "geen sectorwoorden"} />
                )}
              </WoordRij>
            ))
          )}
        </Card>
      </Blok>

      <Blok
        titel="Register van zoekvragen"
        rechts={!z.register.bestaat ? <ReasonBadge text="nog niet gebouwd" tone="amber" /> : undefined}
      >
        <Card style={{ padding: "14px 18px" }}>
          <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>
            {z.register.reden}
          </p>
        </Card>
      </Blok>

      <Blok titel="Wat de vragen opleverden">
        <p style={{ ...noot, marginTop: 0, marginBottom: 10 }}>
          Per vraagfamilie — vragen met dezelfde zoekinstelling, hoek en reikwijdte — opgeteld over
          de oogstruns hieronder.
        </p>
        <Families families={z.families} reden={z.familiesReden} />
      </Blok>

      <Blok titel="Oogstruns">
        <Runs runs={z.runs} reden={z.familiesReden} />
      </Blok>
    </div>
  );
}

function Deel({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div style={{ padding: "8px 0" }}>
      <h3
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          margin: "6px 0 2px",
        }}
      >
        {titel}
      </h3>
      {children}
    </div>
  );
}

/** Een naam links, de woorden rechts; op een smal scherm onder elkaar. */
function WoordRij({ naam, children }: { naam: ReactNode; children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 14px",
        padding: "10px 0",
        borderTop: `1px solid ${color.lineFaint}`,
        alignItems: "baseline",
      }}
    >
      <div
        style={{
          flex: "0 0 180px",
          fontSize: size.meta,
          fontWeight: weight.medium,
          color: color.ink,
        }}
      >
        {naam}
      </div>
      <div style={{ flex: "1 1 260px", minWidth: 0 }}>{children}</div>
    </div>
  );
}

/** Woorden als chips, precies zoals de server ze stuurt. */
function Woorden({ woorden, leeg }: { woorden: readonly string[]; leeg: string }) {
  if (woorden.length === 0) return <EmptyReason reason={leeg} />;
  return (
    <ul
      style={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
      }}
    >
      {woorden.map((w, i) => (
        <li key={`${i}-${w}`} style={chip}>
          {w}
        </li>
      ))}
    </ul>
  );
}

const KOPPEN: readonly (readonly [string, boolean])[] = [
  ["Zoekmachine", false],
  ["Hoek", false],
  ["Reikwijdte", false],
  ["Vragen", true],
  ["Runs", true],
  ["Gevonden", true],
  ["Nieuw", true],
  ["Door de keuring", true],
  ["Fouten", true],
  ["Uitkomst", false],
  ["Laatst", false],
];

function Families({
  families,
  reden,
}: {
  families: readonly Zoekfamilie[];
  reden: string | null;
}) {
  const [alle, setAlle] = useState(false);
  if (families.length === 0) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason={reden ?? "de server stuurde geen vraagfamilies"} />
      </Card>
    );
  }
  const zichtbaar = alle ? families : families.slice(0, EERST);
  return (
    <>
      {/* Families én een reden: dan telde een deel van de telemetrie niet mee. */}
      {reden && (
        <p role="note" style={{ fontSize: size.meta, color: color.accentAmber, marginBottom: 10 }}>
          {reden}
        </p>
      )}
      <Tabel label="Wat elke vraagfamilie opleverde" minBreedte={1120}>
        <thead>
          <tr>
            {KOPPEN.map(([kop, rechts]) => (
              <th key={kop} scope="col" style={kopCel(rechts)}>
                {kop}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {zichtbaar.map((f, i) => (
            // Twee zoekinstellingen van dezelfde machine kunnen een familie met
            // dezelfde naam dragen: de plek maakt de sleutel uniek.
            <FamilieRij key={`${i}-${f.zoekmachine}-${f.familie}`} f={f} />
          ))}
        </tbody>
      </Tabel>
      {families.length > EERST && (
        <button
          type="button"
          aria-expanded={alle}
          onClick={() => setAlle((a) => !a)}
          style={{ ...knopLicht, marginTop: 10 }}
        >
          {alle ? "Toon er minder" : `Toon alle ${getal(families.length)}`}
        </button>
      )}
    </>
  );
}

function FamilieRij({ f }: { f: Zoekfamilie }) {
  return (
    <tr data-familie-rij>
      <td style={cel()}>{f.zoekmachine}</td>
      <td style={cel()}>{woordOfReden(f.hoekLabel, f.hoek, "hoek")}</td>
      <td style={cel()}>
        <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          {woordOfReden(f.reikwijdteLabel, f.reikwijdte, "reikwijdte")}
          {f.pdf && <ReasonBadge text="pdf" title="Vragen die alleen naar pdf-documenten zoeken." />}
        </span>
      </td>
      <td style={cel(true)}>{getal(f.vragen)}</td>
      <td style={cel(true)}>{getal(f.runs)}</td>
      <td style={cel(true)}>{getal(f.gevonden)}</td>
      <td style={cel(true)}>{getal(f.nieuw)}</td>
      <td style={cel(true)}>{getal(f.doorKeuring)}</td>
      <td style={cel(true)}>
        <span style={{ color: f.fouten > 0 ? color.accentRed : color.faint }}>{getal(f.fouten)}</span>
      </td>
      <td style={{ ...cel(), minWidth: 220 }}>{f.tekst}</td>
      <td style={{ ...cel(), whiteSpace: "nowrap" }}>
        {datum(f.laatst) ?? <EmptyReason reason="tijdstip niet vastgelegd" />}
      </td>
    </tr>
  );
}

function Runs({ runs, reden }: { runs: Zoek["runs"]; reden: string | null }) {
  if (runs.length === 0) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason={reden ?? "de server noemt geen oogstrun"} />
      </Card>
    );
  }
  // De naam van een run ("disc-20260806-backfill") is intern; de datum en het
  // aantal vragen zeggen de beheerder wat hij moet weten.
  return (
    <Tabel label="Oogstruns met telemetrie" minBreedte={320}>
      <thead>
        <tr>
          <th scope="col" style={kopCel()}>
            Datum
          </th>
          <th scope="col" style={kopCel(true)}>
            Vragen
          </th>
        </tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.run} data-run>
            <td style={cel()}>
              {datum(r.datum) ?? <EmptyReason reason="datum niet af te leiden" />}
            </td>
            <td style={cel(true)}>{getal(r.vragen)}</td>
          </tr>
        ))}
      </tbody>
    </Tabel>
  );
}

const chip = {
  fontSize: 12,
  color: color.body,
  background: color.lineFaint,
  border: `1px solid ${color.line}`,
  borderRadius: radius.pill,
  padding: "2px 9px",
  whiteSpace: "nowrap",
} as const;
